import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

interface MenuItem {
  path?: string
  label: string
  icon: string
  children?: MenuItem[]
}

const Sidebar = () => {
  const location = useLocation()
  const { hasPermission } = useAuth()
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Configuración'])

  const allMenuItems: MenuItem[] = [
    { path: '/dashboard', label: 'Principal', icon: '📊' },
    { path: '/clients', label: 'Clientes', icon: '👥' },
    { path: '/properties', label: 'Inmuebles', icon: '🏠' },
    { path: '/contracts', label: 'Contratos', icon: '📄' },
    { path: '/payments', label: 'Pagos', icon: '💰' },
    { path: '/receipts', label: 'Recibos', icon: '🧾' },
    { path: '/dgi/pending', label: 'Facturación', icon: '📑' },
    { path: '/reports', label: 'Reportes', icon: '📊' },
    {
      label: 'Configuración',
      icon: '⚙️',
      children: [
        { path: '/adjustments', label: 'Reajustes', icon: '📈' },
        { path: '/users', label: 'Usuarios', icon: '👤' },
        { path: '/settings', label: 'Sistema', icon: '⚙️' },
        { path: '/admin/garantias', label: 'Garantías', icon: '🗂️' }
      ]
    }
  ]

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => {
    // Hide "Configuración" menu for users without users.read permission
    if (item.label === 'Configuración' && !hasPermission('users', 'read')) {
      return false
    }
    // Hide "Facturación" for users without dgi.read permission
    if (item.label === 'Facturación' && !hasPermission('dgi', 'read')) {
      return false
    }
    return true
  })

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    )
  }

  const isActiveParent = (item: MenuItem): boolean => {
    if (item.children) {
      return item.children.some((child) => child.path && location.pathname.startsWith(child.path))
    }
    return false
  }

  return (
    <aside className="w-64 bg-gray-800 min-h-screen">
      <nav className="mt-5 px-2">
        {menuItems.map((item) => (
          <div key={item.label}>
            {item.children ? (
              <>
                <button
                  onClick={() => toggleMenu(item.label)}
                  className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                    isActiveParent(item)
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <span className="mr-3">{item.icon}</span>
                  <span className="flex-1 text-left">{item.label}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      expandedMenus.includes(item.label) ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {expandedMenus.includes(item.label) && (
                  <div className="ml-4 mb-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path!}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                          location.pathname.startsWith(child.path!)
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        <span className="mr-3">{child.icon}</span>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.path!}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1 ${
                  location.pathname.startsWith(item.path!)
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </Link>
            )}
          </div>
        ))}
      </nav>
    </aside>
  )
}

export default Sidebar
